import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import type { ConfirmationPreviewResponse } from '@ooh/contracts';
import { offerQueries } from './queries';
import type { WithCallWithAuth } from './queries';

function useEffectiveAuth(): WithCallWithAuth {
  const { auth } = useRouteContext({ from: '__root__' });
  // RouterContext['auth'] is structurally identical to WithCallWithAuth but TS cannot
  // infer the narrowing from useRouteContext's return type — cast is safe, mirroring
  // sale-preview.ts's own useEffectiveAuth.
  return auth as WithCallWithAuth;
}

export interface UseConfirmationPreviewResult {
  isLoading: boolean;
  isError: boolean;
  data: ConfirmationPreviewResponse | undefined;
  refetch: () => void;
}

/**
 * GET /offers/:id/confirmation-preview (offer-approval / "Campaign confirmation
 * preview") — powers the confirm-campaign dialog's pre-commit breakdown
 * (action-breakdown). Mirrors `useSalePreview`'s shape exactly; the response is
 * already the shared `CampaignOutcomeItem[]` vocabulary, so no client-side mapping is
 * needed here beyond exposing loading/error/refetch.
 */
export function useConfirmationPreview(offerId: string): UseConfirmationPreviewResult {
  const auth = useEffectiveAuth();
  const query = useQuery(offerQueries.confirmationPreview(auth, offerId));

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    data: query.data,
    refetch: () => void query.refetch(),
  };
}
